/**
 * Stock Context สำหรับแชร์ state ของ stock ระหว่าง components
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import apiService from '../services/ApiService';

// Initial state
const initialState = {
  batteryStock: [],
  chargerStock: [],
  loading: false,
  error: null
};

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_BATTERY_STOCK: 'SET_BATTERY_STOCK',
  SET_CHARGER_STOCK: 'SET_CHARGER_STOCK',
  ADD_BATTERY_STOCK: 'ADD_BATTERY_STOCK',
  ADD_CHARGER_STOCK: 'ADD_CHARGER_STOCK',
  UPDATE_BATTERY_STOCK: 'UPDATE_BATTERY_STOCK',
  UPDATE_CHARGER_STOCK: 'UPDATE_CHARGER_STOCK',
  DELETE_BATTERY_STOCK: 'DELETE_BATTERY_STOCK',
  DELETE_CHARGER_STOCK: 'DELETE_CHARGER_STOCK'
};

// Reducer
const stockReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case ACTIONS.SET_BATTERY_STOCK:
      return { ...state, batteryStock: action.payload, loading: false, error: null };
    case ACTIONS.SET_CHARGER_STOCK:
      return { ...state, chargerStock: action.payload, loading: false, error: null };
    case ACTIONS.ADD_BATTERY_STOCK:
      return { 
        ...state, 
        batteryStock: [action.payload, ...state.batteryStock], 
        loading: false, 
        error: null 
      };
    case ACTIONS.ADD_CHARGER_STOCK:
      return { 
        ...state, 
        chargerStock: [action.payload, ...state.chargerStock], 
        loading: false, 
        error: null 
      };
    case ACTIONS.UPDATE_BATTERY_STOCK:
      return {
        ...state,
        batteryStock: state.batteryStock.map(item => 
          item.id === action.payload.id ? action.payload : item
        ),
        loading: false,
        error: null
      };
    case ACTIONS.UPDATE_CHARGER_STOCK:
      return {
        ...state,
        chargerStock: state.chargerStock.map(item => 
          item.id === action.payload.id ? action.payload : item
        ),
        loading: false,
        error: null
      };
    case ACTIONS.DELETE_BATTERY_STOCK:
      return {
        ...state,
        batteryStock: state.batteryStock.filter(item => item.id !== action.payload),
        loading: false,
        error: null
      };
    case ACTIONS.DELETE_CHARGER_STOCK:
      return {
        ...state,
        chargerStock: state.chargerStock.filter(item => item.id !== action.payload),
        loading: false,
        error: null
      };
    default:
      return state;
  }
};

// Create context
const StockContext = createContext();

// Provider component
export const StockProvider = ({ children }) => {
  const [state, dispatch] = useReducer(stockReducer, initialState);

  // Battery Stock Actions
  const fetchBatteryStock = useCallback(async (searchFilters = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const result = await apiService.getBatteryStock(searchFilters);
      if (result.success) {
        dispatch({ type: ACTIONS.SET_BATTERY_STOCK, payload: result.data });
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error });
      }
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
    }
  }, []);

  const createBatteryStock = useCallback(async (stockData, shouldOverwrite = false) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const result = await apiService.createBatteryStock(stockData, shouldOverwrite);
      if (result.success) {
        dispatch({ type: ACTIONS.ADD_BATTERY_STOCK, payload: result.data });
        return { success: true, data: result.data };
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error });
        return { success: false, error: result.error };
      }
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      return { success: false, error: err.message };
    }
  }, []);

  const updateBatteryStock = useCallback(async (id, stockData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const result = await apiService.updateBatteryStock(id, stockData);
      if (result.success) {
        dispatch({ type: ACTIONS.UPDATE_BATTERY_STOCK, payload: result.data });
        return { success: true, data: result.data };
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error });
        return { success: false, error: result.error };
      }
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      return { success: false, error: err.message };
    }
  }, []);

  const deleteBatteryStock = useCallback(async (id) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const result = await apiService.deleteBatteryStock(id);
      if (result.success) {
        dispatch({ type: ACTIONS.DELETE_BATTERY_STOCK, payload: id });
        return { success: true };
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error });
        return { success: false, error: result.error };
      }
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      return { success: false, error: err.message };
    }
  }, []);

  // Charger Stock Actions
  const fetchChargerStock = useCallback(async (searchFilters = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const result = await apiService.getChargerStock(searchFilters);
      if (result.success) {
        dispatch({ type: ACTIONS.SET_CHARGER_STOCK, payload: result.data });
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error });
      }
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
    }
  }, []);

  const createChargerStock = useCallback(async (stockData, shouldOverwrite = false) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const result = await apiService.createChargerStock(stockData, shouldOverwrite);
      if (result.success) {
        dispatch({ type: ACTIONS.ADD_CHARGER_STOCK, payload: result.data });
        return { success: true, data: result.data };
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error });
        return { success: false, error: result.error };
      }
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      return { success: false, error: err.message };
    }
  }, []);

  const updateChargerStock = useCallback(async (id, stockData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const result = await apiService.updateChargerStock(id, stockData);
      if (result.success) {
        dispatch({ type: ACTIONS.UPDATE_CHARGER_STOCK, payload: result.data });
        return { success: true, data: result.data };
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error });
        return { success: false, error: result.error };
      }
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      return { success: false, error: err.message };
    }
  }, []);

  const deleteChargerStock = useCallback(async (id) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const result = await apiService.deleteChargerStock(id);
      if (result.success) {
        dispatch({ type: ACTIONS.DELETE_CHARGER_STOCK, payload: id });
        return { success: true };
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: result.error });
        return { success: false, error: result.error };
      }
    } catch (err) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: err.message });
      return { success: false, error: err.message };
    }
  }, []);

  const value = {
    // State
    batteryStock: state.batteryStock,
    chargerStock: state.chargerStock,
    loading: state.loading,
    error: state.error,
    
    // Battery Stock Actions
    fetchBatteryStock,
    createBatteryStock,
    updateBatteryStock,
    deleteBatteryStock,
    
    // Charger Stock Actions
    fetchChargerStock,
    createChargerStock,
    updateChargerStock,
    deleteChargerStock
  };

  return (
    <StockContext.Provider value={value}>
      {children}
    </StockContext.Provider>
  );
};

// Custom hook to use the context
export const useStock = () => {
  const context = useContext(StockContext);
  if (!context) {
    throw new Error('useStock must be used within a StockProvider');
  }
  return context;
};

export default StockContext;